import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import { Link } from 'components/router';

import { useTopicContent } from 'components/pages/topics/TopicPage/TopicContentContext';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';

import {
  cardVariants,
  containerVariants,
  heroVariants,
  sideContentVariants
} from './animations';
import { HeroTalk, LoadingState, RibbonTalk } from './components';
import {
  useAdManagement,
  useContentData,
  useLayoutCalculations
} from './hooks';
import { createTrackClick } from './utils';

/**
 * Props for `TopicHero`.
 */
export type TopicHeroProps = SliceComponentProps<Content.TopicHeroSlice>;

/**
 * Component for "TopicHero" Slices.
 */
const TopicHero = ({ slice }: TopicHeroProps): JSX.Element => {
  const isMobileWidth = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });
  const { setHeroContentIds } = useTopicContent();

  const leftColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);

  // Ad state managed in main component to avoid circular dependencies
  const [isAdBlocked, setIsAdBlocked] = useState(false);
  const [isAdFilled, setIsAdFilled] = useState(false);
  const [isAdLoading, setIsAdLoading] = useState(false);

  // Custom hooks for data and functionality
  const { contentData, contentIds, isLoading, heroTalk, ribbonTalks } =
    useContentData({
      sailthruSectionId: slice.primary.sailthru_section_id || undefined
    });

  const showAdUnit = !!(
    slice.primary.enable_ad_unit && slice.primary.ad_tag_id
  );

  const { handleAdFillChange } = useAdManagement({
    showAdUnit,
    isAdBlocked,
    isAdFilled,
    isAdLoading,
    setIsAdBlocked,
    setIsAdFilled,
    setIsAdLoading
  });

  const { visibleTalkCount, shouldShowAdInRibbon } = useLayoutCalculations({
    leftColumnRef,
    rightColumnRef,
    contentData,
    ribbonTalks,
    showAdUnit,
    isAdBlocked,
    isAdFilled,
    isAdLoading,
    hasSeeAllLink: !!slice.primary.see_all_link
  });

  // Reset ad states when ad becomes visible again after being hidden
  useEffect(() => {
    if (shouldShowAdInRibbon && showAdUnit && !isAdBlocked) {
      setIsAdFilled(false);
      setIsAdLoading(true);
    }
  }, [shouldShowAdInRibbon, showAdUnit, isAdBlocked]);

  // Update context with content IDs when they change
  useEffect(() => {
    setHeroContentIds(contentIds);
  }, [contentIds, setHeroContentIds]);

  const trackClick = createTrackClick(slice.primary.hero_title || undefined);

  if (isLoading) {
    return (
      <LoadingState
        sliceType={slice.slice_type}
        sliceVariation={slice.variation}
      />
    );
  }

  if (isEmpty(contentData)) {
    return <></>;
  }

  const displayedRibbonTalks = isMobileWidth
    ? ribbonTalks.slice(0, 4) // Cap mobile to 4 talks
    : ribbonTalks.slice(0, visibleTalkCount);

  return (
    <motion.section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Top Border Section */}
      {slice.primary.section_title && (
        <ContentContainer className="relative" noPadding={isMobileWidth}>
          <motion.div
            className="flex items-center justify-between border-t-thin border-black pb-4 pt-3 xs-tui:pl-5 sm-tui:pl-0"
            variants={heroVariants}
          >
            <Text variant="label2" useNewTextStyles isBold>
              {slice.primary.section_title}
            </Text>
          </motion.div>
        </ContentContainer>
      )}

      {/* Title Section */}
      <ContentContainer className="relative pb-12" noPadding={isMobileWidth}>
        <motion.div
          id="topic-hero-header"
          className={cx(
            'border-t-thin border-black/16 pb-6 sm-tui:pt-3 md-tui:pb-8 md-tui:pt-3 lg-tui:pt-4 xl-tui:pt-5',
            {
              'grid grid-cols-2 gap-10': !isMobileWidth,
              'flex flex-col gap-0 px-5': isMobileWidth
            }
          )}
          variants={heroVariants}
        >
          <motion.div
            className="sm:pb-4 sm:pt-6 md:py-0"
            variants={heroVariants}
          >
            <Text
              tag="p"
              variant="display"
              useNewTextStyles
              UNSAFE_className="font-semibold"
            >
              {slice.primary.hero_title}
            </Text>
          </motion.div>

          <motion.div className="w-full" variants={heroVariants}>
            <Text
              tag="p"
              UNSAFE_className="md-tui:invisible sm:text-textTertiary-onLight md:text-textPrimary-onLight"
              variant="subheader2"
              useNewTextStyles
            >
              {slice.primary.hero_description}
            </Text>
          </motion.div>
        </motion.div>

        {/* Main Content Layout */}
        <div className="flex flex-col gap-5 lg-tui:flex-row lg-tui:divide-x-thin">
          {/* Hero Talk - Left Side */}
          <HeroTalk heroTalk={heroTalk} leftColumnRef={leftColumnRef} />

          {/* Right Column - Editorial Style with Ad */}
          <motion.div
            className="@container w-full px-4 md-tui:px-0 lg-tui:w-80"
            variants={sideContentVariants}
            ref={rightColumnRef}
          >
            <div className="grid grid-cols-1 gap-6 divide-y-thick md-tui:pl-5">
              {displayedRibbonTalks.map((talk, index) => {
                const currentWidth =
                  typeof window !== 'undefined' ? window.innerWidth : 1200;
                const isAdVisible =
                  showAdUnit &&
                  !isAdBlocked &&
                  (currentWidth < 808 || currentWidth >= 1055);

                const shouldShowAd =
                  isAdVisible &&
                  shouldShowAdInRibbon &&
                  ((index === 1 && visibleTalkCount >= 2) ||
                    (index === 0 && visibleTalkCount === 1));

                const adCustomParams = slice.primary.ad_custom_params
                  ? Object.fromEntries(
                      slice.primary.ad_custom_params
                        .split(', ')
                        .map(param => param.split('='))
                    )
                  : undefined;

                return (
                  <RibbonTalk
                    key={talk.slug}
                    talk={talk}
                    index={index}
                    trackClick={trackClick}
                    shouldShowAd={shouldShowAd}
                    isAdLoading={isAdLoading}
                    isAdFilled={isAdFilled}
                    adTagId={slice.primary.ad_tag_id || undefined}
                    adCustomParams={adCustomParams}
                    onAdFillChange={handleAdFillChange}
                    shouldShowAdInRibbon={shouldShowAdInRibbon}
                  />
                );
              })}

              {/* See All Link */}
              {slice.primary.see_all_link && slice.primary.see_all_text && (
                <motion.div
                  className="flex justify-end pt-6"
                  variants={cardVariants}
                >
                  <Link href={slice.primary.see_all_link} isZenithLink>
                    <Text
                      variant="link2"
                      UNSAFE_className="underline font-semibold"
                      useNewTextStyles
                      tag="p"
                    >
                      {slice.primary.see_all_text}
                    </Text>
                  </Link>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </ContentContainer>
    </motion.section>
  );
};

export default TopicHero;
